import * as React from 'react'
import {ReactElement, ReactNode} from 'react'
import {AdminPagePropsSet, createAdminPage} from '../shared/hoc/createAdminPage'
import {EquipmentsForPagination, EquipmentsRepository} from './EquipmentsRepository'
import ListSearchFormArea from '../../styledComponents/adminPage/ListSearchFormArea'
import {PageMainArea} from '../shared/hoc/withAdminPageTemplate'
import {Nullable} from '../../app/TypeHelpers'
import SortableTableHeader, {
    SortableTableHeaderCell,
} from '../../styledComponents/adminPage/SortablePaginationTableHeader'
import {SortOrder} from '../shared/SortOrder'
import SortablePaginationTable from '../../styledComponents/adminPage/SortablePaginationTable'
import SortablePaginationTableBody from '../../styledComponents/adminPage/SortablePaginationTableBody'
import {COMMON_CONSTANTS} from '../../CommonConstants'
import EquipmentForAdmin from './EquipmentForAdmin'
import {formatDateTimeForUpdateDate} from '../../formatters/formatDate'
import {NoWrappedTableCell} from '../../styledComponents/adminPage/Table'
import {SearchResultCountDisplayProps} from '../../styledComponents/adminPage/SearchResultCountDisplay'
import {numberOrZero} from '../../stringHelper/stringHelpers'
import {SessionStorageHelper} from '../../SessionStorageHelper'
import {HeadingAreaMessageParams} from '../shared/pageBaseComponents/HeadingAreaMessageComponent'
import {AdminDefaultMediumButton} from '../../styledComponents/adminPage/Buttons'
import SearchPageActionArea from '../../styledComponents/adminPage/SearchPageActionArea'
import {flipSortDirection} from '../shared/SortDirectionHelper'
import {judgeAuthorityCheck} from '../../__NoAutomatedTests/common/util/Natutil'
import {Link, TableCell, TableRow} from '@mui/material'
import {Location} from 'react-router-dom'

const KEY_ROWS_PER_PAGE: string = 'AdminEquipmentsListPage.rowsPerPage'

interface LocationState {
    initialMessage: HeadingAreaMessageParams
}

interface Props {
    equipmentsRepo: EquipmentsRepository
    location: Location<LocationState>
    condominiumCd: string
}

interface State {
    equipments: EquipmentForAdmin[],
    sortOrder: SortOrder,
    totalCount: number
    rowsPerPage: number
    page: number
    editMode: boolean
}

class AdminEquipmentsListPage extends React.Component<Props & AdminPagePropsSet, State> {

    static displayName: string = 'AdminEquipmentsListPage'
    subSortKey: SortOrder = new SortOrder('equipmentId', 'asc')

    constructor(props: Readonly<Props & AdminPagePropsSet>) {
        super(props)
        this.state = {
            equipments: [],
            sortOrder: new SortOrder('updateDatetime', 'desc'),
            rowsPerPage: COMMON_CONSTANTS.ROWS_PER_PAGE_SETTINGS[0],
            totalCount: 0,
            page: COMMON_CONSTANTS.FIRST_PAGE,
            editMode: false,
        }

        this.handleNewEquipmentButtonClick = this.handleNewEquipmentButtonClick.bind(this)
        this.handleChangePage = this.handleChangePage.bind(this)
        this.handleChangeRowsPerPage = this.handleChangeRowsPerPage.bind(this)
        this.handleSortEquipments = this.handleSortEquipments.bind(this)
    }

    async componentDidMount(): Promise<void> {
        const editMode: boolean = judgeAuthorityCheck('EDIT-EQUIPMENT_FACILITY')

        const rowsPerPage: number = this.setupRowsPerPage()

        const equipments: EquipmentsForPagination =
            await this.getEquipments(
                this.state.sortOrder,
                rowsPerPage,
                COMMON_CONSTANTS.FIRST_PAGE_OFFSET,
            )

        this.setState({
            equipments: equipments.contents,
            totalCount: equipments.totalContentsCount,
            rowsPerPage: rowsPerPage,
            editMode: editMode,
        })

        this.showInitialMessage()
    }

    setupRowsPerPage(): number {
        let rowsPerPage: number = SessionStorageHelper.getNumberItemWithDefault(
            KEY_ROWS_PER_PAGE,
            COMMON_CONSTANTS.ROWS_PER_PAGE_SETTINGS[0],
        )

        if (!COMMON_CONSTANTS.ROWS_PER_PAGE_SETTINGS.includes(rowsPerPage)) {
            SessionStorageHelper.removeItem(KEY_ROWS_PER_PAGE)
            rowsPerPage = COMMON_CONSTANTS.ROWS_PER_PAGE_SETTINGS[0]
        }

        return rowsPerPage
    }

    showInitialMessage(): void {
        if (!this.props.location || !this.props.location.state) {
            return
        }
        const initialMessage: HeadingAreaMessageParams = this.props.location.state.initialMessage
        this.props.navigate(this.props.location.pathname, {replace: true})
        this.props.eventEmitter.showHeadingAreaMessage(initialMessage)
    }

    beforeUserAction(): void {
        this.props.eventEmitter.clearHeadingAreaMessage()
    }

    async getEquipments(sortOrder: SortOrder, limit: number, offset: number): Promise<EquipmentsForPagination> {
        await this.props.eventEmitter.showLoading()
        let equipments: Nullable<EquipmentsForPagination>
        try {
            const {equipmentsRepo, condominiumCd} = this.props
            equipments = await this.props.asyncCallFunction(
                equipmentsRepo.getEquipments.bind(
                    equipmentsRepo,
                    condominiumCd,
                    {sortOrders: [sortOrder, this.subSortKey], limit: limit, offset: offset},
                ),
                (httpErrorOrNetworkError) => {
                    httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
                },
            )
        } finally {
            this.props.eventEmitter.closeLoading()
        }
        if (equipments == null) {
            return {totalContentsCount: 0, contents: []}
        }
        return equipments
    }

    async handleChangePage(
        event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
        newPage: number,
    ): Promise<void> {
        this.beforeUserAction()

        const offset: number = newPage * this.state.rowsPerPage
        const equipments: EquipmentsForPagination =
            await this.getEquipments(
                this.state.sortOrder,
                this.state.rowsPerPage,
                offset,
            )
        this.setState({
            totalCount: equipments.totalContentsCount,
            page: newPage,
            equipments: equipments.contents,
        }, () => {
            this.props.eventEmitter.scrollToContentsTop()
        })
    }

    async handleChangeRowsPerPage(event: React.ChangeEvent<HTMLInputElement>): Promise<void> {
        this.beforeUserAction()

        const rowsPerPage: number = numberOrZero(event.target.value)

        SessionStorageHelper.setItem(KEY_ROWS_PER_PAGE, `${rowsPerPage}`)

        const equipments: EquipmentsForPagination =
            await this.getEquipments(
                this.state.sortOrder,
                rowsPerPage,
                COMMON_CONSTANTS.FIRST_PAGE_OFFSET,
            )
        this.setState({
            equipments: equipments.contents,
            totalCount: equipments.totalContentsCount,
            rowsPerPage: rowsPerPage,
            page: COMMON_CONSTANTS.FIRST_PAGE,
        }, () => {
            this.props.eventEmitter.scrollToContentsTop()
        })
    }

    async handleSortEquipments(sortKey: string): Promise<void> {
        this.beforeUserAction()

        const newSortOrder: SortOrder = new SortOrder(
            sortKey,
            flipSortDirection(this.state.sortOrder.sortDirection, 'asc'),
        )
        const equipments: EquipmentsForPagination =
            await this.getEquipments(
                newSortOrder,
                this.state.rowsPerPage,
                COMMON_CONSTANTS.FIRST_PAGE_OFFSET,
            )

        this.setState({
            equipments: equipments.contents,
            sortOrder: newSortOrder,
            totalCount: equipments.totalContentsCount,
            page: COMMON_CONSTANTS.FIRST_PAGE,
        })
    }

    handleNewEquipmentButtonClick(): void {
        this.props.navigate(
            `/admin/condominiums/${this.props.condominiumCd}/equipments/register`,
        )
    }

    searchResults(): SearchResultCountDisplayProps {
        if (this.state.equipments.length === 0) {
            return {noResultMessage: '設備は登録されていません。'}
        }

        return {
            totalCount: this.state.totalCount,
            rowsPerPage: this.state.rowsPerPage,
            page: this.state.page,
        }
    }

    renderEquipmentsTable(equipments: EquipmentForAdmin[]): Nullable<ReactElement> {
        if (equipments.length === 0) {
            return null
        }
        const headers: SortableTableHeaderCell[] = [
            {id: 'name', label: '設備名'},
            {id: 'parentEquipmentName', label: '親設備'},
            {id: 'reservationCountMonthly', label: '予約可能回数(日)'},
            {id: 'reservationCountDaily', label: '予約可能回数(月)'},
            {id: 'lotteryEntryCountMonthly', label: '抽選申込数'},
            {id: 'updateDatetime', label: '更新日時', sortable: true, direction: this.state.sortOrder.sortDirection},
        ]
        return (
            <SortablePaginationTable
                paginationProps={{
                    count: this.state.totalCount,
                    rowsPerPage: this.state.rowsPerPage,
                    page: this.state.page,
                    onPageChange: this.handleChangePage,
                    onRowsPerPageChange: this.handleChangeRowsPerPage,
                }}
            >
                <SortableTableHeader
                    noWrap
                    headers={headers}
                    onSortOrderChange={this.handleSortEquipments}
                />
                <SortablePaginationTableBody>
                    {this.state.equipments.map(equipment => this.renderEquipmentRecord(equipment))}
                </SortablePaginationTableBody>
            </SortablePaginationTable>
        )

    }

    renderEquipmentRecord(equipment: EquipmentForAdmin): ReactElement {
        const updatedDateTime: string = formatDateTimeForUpdateDate(equipment.updateDatetime)
        const equipmentUrl: string = `/admin/condominiums/${this.props.condominiumCd}/equipments/${equipment.id}`
        return (
            <TableRow key={equipment.id}>
                <TableCell><Link href={equipmentUrl} underline="hover">{equipment.name}</Link></TableCell>
                <TableCell>{equipment.parentEquipmentName}</TableCell>
                <TableCell align={'right'}>{equipment.reservationCountDaily}</TableCell>
                <TableCell align={'right'}>{equipment.reservationCountMonthly}</TableCell>
                <TableCell align={'right'}>{equipment.lotteryEntryCountMonthly}</TableCell>
                <NoWrappedTableCell align={'center'}>{updatedDateTime}</NoWrappedTableCell>
            </TableRow>
        )
    }

    render(): ReactNode {
        return (
            <>
                <ListSearchFormArea
                    searchResult={this.searchResults()}
                />
                {
                    this.state.editMode &&
                    <SearchPageActionArea>
                        <AdminDefaultMediumButton onClick={this.handleNewEquipmentButtonClick}>
                            新規
                        </AdminDefaultMediumButton>
                    </SearchPageActionArea>
                }
                <PageMainArea>
                    {this.renderEquipmentsTable(this.state.equipments)}
                </PageMainArea>
            </>
        )
    }
}

export default createAdminPage(AdminEquipmentsListPage)
